import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import { Col, Row, ListGroup, Badge } from '../../../ui-kit';
import { IconCircleInfo, IconPersonRunning } from '../../../ui-kit/icons';
import { selectCurrentDisplayedIncident } from '../../../../state/modules/incidents/selectors';
import SkillIconList from '../../../general/SkillIconList';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import { secondsToHumanTime } from '../../../../utils/time';
import AvailabilityIcon from '../../../general/AvailabilityIcon';
import { IncidentResponderStatusIcon } from '../../../general/IncidentResponderStatusIcon';
import Settings from './Settings';

// Defines the color of the list item
const statusToStyle = {
  acknowledged: 'success',
  pending: null,
  rejected: 'danger',
  expired: 'danger'
};

// Highlights the content in blue, with white font
const onDutyStyle = {
  background: '#5959fc',
  color: '#fff',
  borderRadius: '5px',
  padding: '3px'
};

const ResponderList = ({ responders, title, variant, showEstimatedTimeOfArrival }) => {
  if (responders.length === 0) return null;
  return (
    <ListGroup className="mb-1">
      <ListGroup.Item className="d-flex justify-content-between align-items-start" variant={variant}>
        <h5 className="fw-semibold"> {title}</h5>
        <Badge variant="primary" pill>
          {responders.length}
        </Badge>
      </ListGroup.Item>
      {responders.map(responder => (
        <ResponderItem
          responder={responder}
          key={responder.id}
          showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        />
      ))}
    </ListGroup>
  );
};

// Split responders into groups
const splitIntoGroups = (responders, hideRejectedAndExpiredResponses) => {
  // sort so that users with a shift are always on top of each sublist
  responders = orderBy(responders, r => r.activeStandbyDutyFunctions.length, 'desc');

  let hiddenResponsesCount = 0;

  if (hideRejectedAndExpiredResponses) {
    hiddenResponsesCount = responders.filter(r => r.status === 'rejected' || r.status === 'expired').length;
    responders = responders.filter(r => r.status !== 'rejected' && r.status !== 'expired');
  }

  let acknowledged = responders.filter(r => r.status === 'acknowledged');
  // sort so the users that where available before the incident are always on top
  acknowledged = orderBy(acknowledged, 'available_at_incident_creation', 'desc');
  const acknowledgedAvailableTeams = acknowledged.filter(r => r.group_available_at_incident_creation);
  const acknowledgedUnavailableTeams = acknowledged.filter(r => !r.group_available_at_incident_creation);

  const pending = responders.filter(r => r.status === 'pending');
  const pendingUnavailableCount = pending.filter(r => !r.available_at_incident_creation).length;
  const rejected = responders.filter(r => r.status === 'rejected');
  // We only care about those expired who were available. Expired unavailable
  // clutter the list.
  // Note: We do not apply this filter to rejected unavailable, becase rejecting
  // requires an explicit action, while expiration happens automatically.
  const expired = responders.filter(r => r.status === 'expired' && r.available_at_incident_creation);

  // Get number of members who where not available at the moment of the incident
  const pendingAvailable = pending.filter(r => r.available_at_incident_creation);

  return {
    acknowledgedAvailableTeams,
    acknowledgedUnavailableTeams,
    pendingAvailable,
    pendingUnavailableCount,
    hiddenResponsesCount,
    rejected,
    expired
  };
};

const InfoMessage = ({ messageI18nKey, count }) => {
  if (count === 0) return null;
  return (
    <ListGroup.Item variant="info">
      <Row>
        <Col lg={1} md={1} sm={1}>
          <IconCircleInfo />
        </Col>
        <Col lg={11} md={11} sm={11}>
          {I18n.t(messageI18nKey, { count })}
        </Col>
      </Row>
    </ListGroup.Item>
  );
};

const ResponderItem = ({ responder, showEstimatedTimeOfArrival, nameCols = showEstimatedTimeOfArrival ? 4 : 6 }) => (
  <ListGroup.Item variant={statusToStyle[responder.status]} className="responder-item">
    <Row>
      <Col lg={1} md={1} sm={1}>
        <IncidentResponderStatusIcon status={responder.status} />
      </Col>
      <Col lg={nameCols} md={nameCols} sm={nameCols}>
        <span style={responder.activeStandbyDutyFunctions.length > 0 ? onDutyStyle : {}}>
          {responder.user_name} <SkillIconList skills={responder.activeStandbyDutyFunctions} />
        </span>
      </Col>
      <Col lg={1} md={1} sm={1}>
        {responder.status === 'acknowledged' && (
          <AvailabilityIcon available={responder.available_at_incident_creation} />
        )}
      </Col>
      <Col lg md sm>
        <SkillIconList skills={responder.skills} />
      </Col>
      {showEstimatedTimeOfArrival && (
        <Col className="text-end" lg md sm>
          {responder.status === 'acknowledged' && responder.estimated_time_of_arrival && (
            <>
              <Badge>
                <IconPersonRunning /> ETA
              </Badge>{' '}
              {secondsToHumanTime(responder.estimated_time_of_arrival)}
            </>
          )}
        </Col>
      )}
    </Row>
  </ListGroup.Item>
);

const CrewConfirmations = ({ displayedIncident, dashboardAPIManager, hideRejectedAndExpiredResponses }) => {
  let responders = useMemo(
    () => displayedIncident?.incident_responses || [],
    // avoid infinite update loop by doing a deep equals
    [JSON.stringify(displayedIncident?.incident_responses)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // load missing memberships, skills and duty functions for the active incident
  useEffect(() => {
    if (responders.length === 0) return;

    const skillIds = responders.flatMap(r => r.skill_ids);
    dashboardAPIManager.fetchSkills({ ids: skillIds, onlyMissing: true });

    const membershipIds = responders.map(r => r.membership_id);
    dashboardAPIManager.fetchMemberships({ ids: membershipIds, onlyMissing: true });

    const dutyFunctionIds = responders.flatMap(r => r.active_duty_function_ids);
    dashboardAPIManager.fetchStandbyDutyFunctions({ ids: dutyFunctionIds, onlyMissing: true });
  }, [responders, dashboardAPIManager]);

  if (!displayedIncident) return <WaitingForIncidentMessage />;

  if (responders.length === 0) {
    return (
      <ListGroup>
        <ListGroup.Item>{I18n.t('dashboards.widget_archetypes.crew_confirmations.no_responses')}</ListGroup.Item>
      </ListGroup>
    );
  }

  // Split responders into groups
  const {
    acknowledgedAvailableTeams,
    acknowledgedUnavailableTeams,
    pendingAvailable,
    pendingUnavailableCount,
    hiddenResponsesCount,
    rejected,
    expired
  } = splitIntoGroups(responders, hideRejectedAndExpiredResponses);

  const translations = I18n.t('classy_enum.incidents/incident_response_status');

  const showEstimatedTimeOfArrival = responders.some(r => r.estimated_time_of_arrival && r.status === 'acknowledged');

  return (
    <>
      <ResponderList
        responders={acknowledgedAvailableTeams}
        title={translations.acknowledged}
        variant="success"
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
      />
      <ResponderList
        responders={acknowledgedUnavailableTeams}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        title={translations.acknowledged_unavailable_team}
        variant="success"
      />
      <ResponderList
        responders={pendingAvailable}
        title={translations.pending}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
      />

      <ListGroup className="mb-1">
        <InfoMessage messageI18nKey="incidents.monitor.unavailable_responses_info" count={pendingUnavailableCount} />
        <InfoMessage
          messageI18nKey="dashboards.widget_archetypes.crew_confirmations.hidden_rejected_and_expired_responses_info"
          count={hiddenResponsesCount}
        />
      </ListGroup>

      <ResponderList
        responders={expired}
        title={translations.expired}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
      />
      <ResponderList
        responders={rejected}
        title={translations.rejected}
        variant="danger"
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
      />
    </>
  );
};

Object.assign(CrewConfirmations, {
  defaultSettings: {
    defaultAutoScrollEnabled: true,
    hideRejectedAndExpiredResponses: false
  },
  SettingsContent: Settings
});

const mapStateToProps = state => ({
  displayedIncident: selectCurrentDisplayedIncident(state)
});

// Export without redux for testing
export { CrewConfirmations };

export default connect(mapStateToProps)(CrewConfirmations);
